// Pure reconcile math — shared between InvoiceService / InvoiceGenerationService
// and the FE form helpers. No DB calls, no React, no DS imports.
//
// The same tolerance bands fire in three places: the reconcile report builder
// (`reportBuilder.ts`), the matching engine (`matching/`), and the FE form
// helpers (`useReconcileSession`, `InvoiceKpiBand`, `InvoiceCorrectionForm`).
// Drift between them is a class of bug we'd rather not chase, so the
// derivations live here and every consumer reads the same answer.

import { moneyEq, sumAmounts, calculatePricing } from '../../calc.ts';
import { invoiceReconcileStats } from './reconcileStatus.ts';
import type { InvoiceLineItem, InvoiceRow, CorrectionLineItem } from '@shared/types/invoice.ts';
import type { Pricing, PriceType, VatRateCode } from '@shared/types.ts';

// ─── Line-item match status ──────────────────────────────────────────────

export interface LineMismatch {
  net:     number;
  gross:   number;
  netLi:   number;
  grossLi: number;
}

/** Re-exported from `reconcileStatus` so reconcile-math consumers only need
 *  one import. Definition of "matched" lives in reconcileStatus.ts. */
export { isLineCorrectlyMatched as isLineMatched } from './reconcileStatus.ts';

export function computeLineMismatch(li: InvoiceLineItem): LineMismatch | null {
  if (li.links.length === 0) return null;
  const linked = sumAmounts(li.links.map((l) => l.pricing));
  if (moneyEq(linked.net, li.pricing.net_amount)
   && moneyEq(linked.gross, li.pricing.gross_amount)) return null;
  return {
    net:     linked.net,
    gross:   linked.gross,
    netLi:   li.pricing.net_amount,
    grossLi: li.pricing.gross_amount,
  };
}

export function computeLineMismatches(lineItems: InvoiceLineItem[]): Map<number, LineMismatch> {
  const m = new Map<number, LineMismatch>();
  for (const li of lineItems) {
    const mm = computeLineMismatch(li);
    if (mm) m.set(li.id, mm);
  }
  return m;
}

// ─── Totals-level mismatch + unmatched count ─────────────────────────────

export interface TotalsMismatch {
  hasMismatch:    boolean;
  unmatchedCount: number;
}

/** `hasMismatch` is true when the sum of all link pricings diverges from the
 *  invoice's stored totals. `unmatchedCount` counts non-zero line items with
 *  zero links (the operator hasn't picked candidates for them yet). */
export function computeTotalsMismatch(
  lineItems: InvoiceLineItem[],
  totalsNet: number,
  totalsGross: number,
): TotalsMismatch {
  const nonZero = lineItems.filter((li) => Math.abs(li.pricing.net_amount) >= 0.005);
  const unmatched = nonZero.filter((li) => li.links.length === 0).length;
  const allLinks = lineItems.flatMap((li) => li.links);
  if (allLinks.length === 0) return { hasMismatch: false, unmatchedCount: unmatched };
  const linked = sumAmounts(allLinks.map((l) => l.pricing));
  const grossOk = moneyEq(linked.gross, totalsGross);
  const netOk   = moneyEq(linked.net,   totalsNet);
  return { hasMismatch: !grossOk || !netOk, unmatchedCount: unmatched };
}

// ─── KPI band derivation ─────────────────────────────────────────────────

export interface ReconcileKpis {
  totalLines:    number;
  matchedLines:  number;
  reconcilePct:  number;
  paidPct:       number;
  remaining:     number;
}

/** Invoice with the read-only fields the KPI cares about. Loose subset of
 *  InvoiceRow so callers can pass either Invoice (the App type) or
 *  InvoiceWithRelations (the list-view shape). */
export type ReconcileKpiInvoice = Pick<InvoiceRow, 'gross_amount' | 'paid_amount'>;

/** Empty invoice (zero line items) reports 0% rather than 100% — the operator
 *  shouldn't see "fully reconciled" on a still-empty shell. The matched +
 *  reconcilePct fields delegate to `invoiceReconcileStats` so the KPI band
 *  and the InvoiceLineItems section pill can never drift apart. */
export function computeReconcileKpis(
  invoice: ReconcileKpiInvoice,
  lineItems: InvoiceLineItem[],
): ReconcileKpis {
  const gross = invoice.gross_amount ?? 0;
  const paid  = invoice.paid_amount  ?? 0;
  const { total: totalLines, matched: matchedLines, pct: reconcilePct } =
    invoiceReconcileStats(lineItems);
  const paidPct   = gross > 0 ? Math.round((paid / gross) * 100) : 0;
  const remaining = round2(gross - paid);
  return { totalLines, matchedLines, reconcilePct, paidPct, remaining };
}

// ─── Correction-form diff ────────────────────────────────────────────────
//
// Operator edits a subset of correction rows; we project each row to its
// post-edit pricing via calculatePricing, then sum the deltas vs the
// `original` snapshot stored on each `CorrectionLineItem.pricing`.

export interface CorrectionEditRow {
  transaction_id: number;
  weight:         number;
  unit_price:     number;
  price_type:     PriceType;
  vat_rate_code:  VatRateCode;
  gtu_code:       string | null;
  removed:        boolean;
}

export interface CorrectionDiff {
  net:    number;
  vat:    number;
  gross:  number;
  hasChanges:   boolean;
  hasZeroedRow: boolean;
  removedCount: number;
}

export function computeCorrectionDiff(
  editRows: Map<number, CorrectionEditRow>,
  all: CorrectionLineItem[],
  currency: string,
): CorrectionDiff {
  const activeAfter:  Pricing[] = [];
  const activeBefore: Pricing[] = [];
  let removedCount = 0;
  let hasZeroedRow = false;
  let hasChanges   = false;

  for (const ct of all) {
    const row = editRows.get(ct.transaction_id);
    if (!row) continue;
    const before = ct.pricing.original ?? ct.pricing;
    if (row.removed) {
      removedCount += 1;
      hasChanges = true;
      continue;
    }
    const after = effectivePricing(row, currency);
    // Zeroing-by-edit is forbidden — the operator must mark the row as
    // removed instead, so the correction unambiguously documents intent.
    if (after.net_amount === 0 && before.net_amount !== 0) hasZeroedRow = true;
    if (rowChanged(row, ct)) hasChanges = true;
    activeAfter.push(after);
    activeBefore.push(before);
  }

  const a = sumAmounts(activeAfter);
  const b = sumAmounts(activeBefore);
  return {
    net:   round2(a.net   - b.net),
    vat:   round2(a.vat   - b.vat),
    gross: round2(a.gross - b.gross),
    hasChanges,
    hasZeroedRow,
    removedCount,
  };
}

export function effectivePricing(row: CorrectionEditRow, currency: string): Pricing {
  return calculatePricing({
    weight:        row.weight,
    unit_price:    row.unit_price,
    price_type:    row.price_type,
    vat_rate_code: row.vat_rate_code,
    currency,
  });
}

function rowChanged(row: CorrectionEditRow, ct: CorrectionLineItem): boolean {
  const before = ct.pricing.original ?? ct.pricing;
  const weightChanged    = row.price_type === 'per_ton' && row.weight !== before.quantity;
  const unitPriceChanged = row.unit_price !== before.unit_price;
  const vatChanged       = row.vat_rate_code !== before.vat_rate_code;
  const priceTypeChanged = row.price_type !== before.price_type;
  const gtuChanged       = (row.gtu_code || null) !== (ct.gtu_code || null);
  return weightChanged || unitPriceChanged || vatChanged || priceTypeChanged || gtuChanged;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
